import { ResponsiveContainer } from 'recharts';
import { useState } from 'react';
import { RegionStat } from '../../types';

interface RegionalHorizontalBarChartProps {
  data: RegionStat[];
  onCountryClick?: (countryName: string) => void;
  onRegionClick?: (regionName: string) => void;
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))', 
  'hsl(var(--accent))',
  '#8B5CF6',
  '#EC4899',
  '#10B981'
];

export const RegionalHorizontalBarChart = ({ data, onCountryClick, onRegionClick }: RegionalHorizontalBarChartProps) => {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);

  // Group data by region and aggregate countries
  const regionData = data.reduce((acc: any[], curr) => {
    let region = acc.find(r => r.region === curr.region);
    if (!region) {
      region = {
        region: curr.region,
        sessions: 0,
        sites: 0,
        countries: []
      };
      acc.push(region);
    }
    
    region.sessions += curr.sessions;
    region.sites += curr.sites;
    region.countries.push({
      country: curr.country,
      sessions: curr.sessions,
      sites: curr.sites
    });
    
    return acc;
  }, []);

  // Sort by sessions descending
  regionData.sort((a, b) => b.sessions - a.sessions);
  
  // Calculate max sessions for percentage calculations
  const maxSessions = Math.max(...regionData.map(d => d.sessions));

  return (
    <div className="w-full h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-background">
      <div className="space-y-4 p-4 min-h-full">
        {regionData.map((region, index) => {
          const percentage = (region.sessions / maxSessions) * 100;
          const isHovered = hoveredRegion === region.region;
          
          return (
            <div 
              key={region.region}
              className="relative group cursor-pointer"
              onMouseEnter={() => setHoveredRegion(region.region)}
              onMouseLeave={() => setHoveredRegion(null)}
            >
              {/* Region Label */}
              <div className="flex justify-between items-center mb-2">
                <span 
                  className="text-sm font-medium text-foreground hover:text-primary cursor-pointer transition-colors"
                  onClick={() => onRegionClick?.(region.region)}
                >
                  {region.region}
                </span>
                <span className="text-sm text-muted-foreground">
                  {region.sites} sites
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-muted rounded-full h-8 relative overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: COLORS[index % COLORS.length],
                    opacity: hoveredRegion && !isHovered ? 0.4 : 1
                  }}
                />
                {/* Always right-aligned label */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-xs font-medium text-foreground">
                    {region.sessions.toLocaleString()} sessions
                  </span>
                </div>
              </div>
              
              {/* Hover Tooltip */}
              {isHovered && region.countries && region.countries.length > 0 && (
                <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-lg p-3 shadow-lg z-10 min-w-64">
                  <p className="font-medium text-card-foreground mb-2">
                    {region.region === 'North America' ? 'Subregions' : 'Countries'} in {region.region}:
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                     {region.countries
                       .sort((a: any, b: any) => b.sites - a.sites)
                       .map((country: any, idx: number) => (
                          <div 
                            key={idx} 
                            className="flex justify-between text-xs hover:bg-muted/50 p-1 rounded cursor-pointer transition-colors"
                            onClick={() => {
                              if (region.region === 'North America') {
                                // For North America, these are subregions, so use onRegionClick
                                onRegionClick?.(country.country);
                              } else {
                                // For other regions, these are countries, so use onCountryClick
                                onCountryClick?.(country.country);
                              }
                            }}
                          >
                            <span className="text-muted-foreground hover:text-foreground">{country.country}</span>
                            <span className="font-medium hover:text-primary">{country.sites} sites</span>
                          </div>
                       ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};